import { QueryClient } from '@tanstack/react-query';

import {
  getProductsFromOpenSearchResponse,
  ResolvedPrioritization,
  resolveTimeZone,
  sanitizeTreezSearchParams,
} from '@/utils';
import { EnumCustomerType, FulfillmentType, TreezStore } from '@/types';

import { SearchService } from '../services';
import {
  buildQueryFilterFromSearchParams,
  buildSortFilterFromSearchParams,
} from './facets';
import {
  buildProductsSearchQueryKey,
  PRODUCTS_SEARCH_STALE_TIME,
} from './queryKey';
import { buildSearchBodyFromParams } from './transforms';

export async function getOpenSearchServerState({
  queryClient,
  searchParams,
  filters,
  store,
  customerType,
  includePromoItems = false,
  sort,
  revalidate,
  prioritization,
  fulfillmentType,
}: {
  queryClient: QueryClient;
  searchParams: Record<string, string | string[] | boolean[]>;
  filters: Record<string, string | string[] | boolean[]>;
  store?: TreezStore;
  customerType?: EnumCustomerType;
  includePromoItems?: boolean;
  sort?: string;
  revalidate?: number;
  prioritization?: ResolvedPrioritization;
  fulfillmentType?: FulfillmentType;
}) {
  const timeZone = resolveTimeZone(store?.dispensary_time_zone);
  const sanitizedSearchParams = sanitizeTreezSearchParams({
    searchParams,
  });
  const queryFiltersByFacets = buildQueryFilterFromSearchParams(
    sanitizedSearchParams,
    timeZone,
  );

  const fixedQueryFiltersByFacets = buildQueryFilterFromSearchParams(
    filters,
    timeZone,
  );
  const sortFilterFromSearchParams = buildSortFilterFromSearchParams(
    sanitizedSearchParams,
  );
  const defaultSortFilter = buildSortFilterFromSearchParams({
    sort: sort ?? '',
  });

  const { search } = new SearchService({
    entityId: store?.mso_store_entity_id,
    orgId: store?.mso_org_id,
    customerType,
    isStrictMedicalEnabled: store?.strict_medical_enabled,
    timeZone,
    fulfillmentType,
  });

  const { searchBody } = buildSearchBodyFromParams({
    searchParams: sanitizedSearchParams,
    filters,
    includePromoItems,
    prioritization,
    prismicDefaultSort: sort,
  });

  const envCustomerType =
    process.env.NEXT_PUBLIC_FEATURE_FLAG_MED_REC_SELECTION_ACTIVE === 'true'
      ? EnumCustomerType.ADULT
      : EnumCustomerType.NOT_SET;

  const searchResults = await search({ searchBody, revalidate });

  // Client SSR reads zustand initial state (no localStorage): inventory is '' and
  // customer type is the env default, while store falls back to the selectedStore prop.
  const prefetchVariants = [
    { storeName: store?.short_name, customerType },
    { storeName: store?.short_name, customerType: envCustomerType },
    { storeName: undefined, customerType: envCustomerType },
  ];

  await Promise.all(
    prefetchVariants.map(({ storeName, customerType: customerTypeKey }) =>
      queryClient.prefetchQuery({
        queryKey: buildProductsSearchQueryKey({
          storeName,
          customerType: customerTypeKey,
          searchBody,
        }),
        queryFn: async () => searchResults,
        staleTime: PRODUCTS_SEARCH_STALE_TIME,
      }),
    ),
  );

  const products = getProductsFromOpenSearchResponse({
    result: searchResults,
    isSortingApplied: !!sort,
    isSearchingApplied: !!searchParams.query,
    timeZone,
  });

  return {
    products,
    initialQueryFiltersByFacets: queryFiltersByFacets,
    initialFixedQueryFiltersByFacets: fixedQueryFiltersByFacets,
    // if sort is provided from config, we don't want to set it as initial sort to opensearch context
    // it will only be needed for an initial render
    defaultSortFilter,
    initialSortFilter: sortFilterFromSearchParams,
    initialSearchFilter: (searchParams.query as string) || '',
    initialPage: Math.max(
      parseInt((searchParams.page || '1') as string) - 1,
      0,
    ),
    initialSearchResponse: searchResults,
  };
}
